/**
 * FileUploadService.ts
 * Service dédié à la gestion des uploads de fichiers (images, JSON, etc.)
 * Sépare la logique DOM de la logique métier Konva
 */
import { logger } from '../utils/ProfessionalLogger';

export interface FileUploadOptions {
  accept?: string;
  multiple?: boolean;
  onFileSelected: (file: File) => void | Promise<void>;
  onError?: (error: Error) => void;
}

export class FileUploadService {
  private static instance: FileUploadService | null = null;

  private constructor() {
    // Singleton pattern
  }

  /**
   * Obtient l'instance singleton du service
   */
  public static getInstance(): FileUploadService {
    if (!FileUploadService.instance) {
      FileUploadService.instance = new FileUploadService();
    }
    return FileUploadService.instance;
  }

  /**
   * Ouvre un sélecteur de fichier natif et exécute le callback quand un fichier est sélectionné
   * Cette méthode centralise toute la logique DOM pour les uploads
   *
   * @param options - Options de configuration pour l'upload
   * @example
   * ```typescript
   * const fileUploadService = FileUploadService.getInstance();
   * fileUploadService.openFileSelector({
   *   accept: 'image/*',
   *   onFileSelected: async (file) => {
   *     await canvasManager.addImageFromFile(file);
   *   }
   * });
   * ```
   */
  public openFileSelector(options: FileUploadOptions): void {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = options.accept || '*/*';
      input.multiple = options.multiple || false;

      // Gérer la sélection de fichier
      input.onchange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];

        if (!file) {
          return;
        }

        try {
          await options.onFileSelected(file);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Erreur inconnue lors du traitement du fichier';
          logger.error(`❌ Erreur lors du traitement du fichier: ${errorMessage}`, error);

          if (options.onError) {
            options.onError(error instanceof Error ? error : new Error(errorMessage));
          }
        } finally {
          // Nettoyer l'input pour permettre de sélectionner le même fichier à nouveau
          input.value = '';
        }
      };

      // Gérer les erreurs de sélection
      input.oncancel = () => {
        logger.debug("Sélection de fichier annulée par l'utilisateur");
      };

      // Déclencher le sélecteur de fichier natif
      input.click();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erreur lors de l'ouverture du sélecteur de fichier";
      logger.error(`❌ ${errorMessage}`, error);

      if (options.onError) {
        options.onError(error instanceof Error ? error : new Error(errorMessage));
      }
    }
  }

  /**
   * Ouvre un sélecteur d'image spécifiquement
   * Méthode de convenance pour les uploads d'images
   *
   * @param onImageSelected - Callback appelé avec le fichier image sélectionné
   * @param onError - Callback optionnel pour gérer les erreurs
   */
  public openImageSelector(
    onImageSelected: (file: File) => void | Promise<void>,
    onError?: (error: Error) => void
  ): void {
    this.openFileSelector({
      accept: 'image/*',
      onFileSelected: onImageSelected,
      onError,
    });
  }

  /**
   * Ouvre un sélecteur de fichier JSON spécifiquement
   * Méthode de convenance pour les uploads de projets
   *
   * @param onJsonSelected - Callback appelé avec le fichier JSON sélectionné
   * @param onError - Callback optionnel pour gérer les erreurs
   */
  public openJsonSelector(
    onJsonSelected: (file: File) => void | Promise<void>,
    onError?: (error: Error) => void
  ): void {
    this.openFileSelector({
      accept: '.json,application/json',
      onFileSelected: onJsonSelected,
      onError,
    });
  }

  /**
   * Crée une Blob URL à partir d'un fichier (pour les images)
   * PHASE 2: Remplace Base64 par Blob URL pour éviter la consommation excessive de mémoire
   *
   * @param file - Le fichier à convertir
   * @returns Blob URL (ex: "blob:http://localhost:3000/...")
   *
   * @example
   * ```typescript
   * const blobUrl = fileUploadService.createBlobURL(file);
   * // Utiliser blobUrl comme src d'une image
   * // IMPORTANT: Appeler revokeBlobURL(blobUrl) quand l'image n'est plus utilisée
   * ```
   */
  public createBlobURL(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * Révoque une Blob URL pour libérer la mémoire
   * PHASE 2: Nettoyage critique pour éviter les fuites mémoire
   *
   * @param blobURL - La Blob URL à révoquer
   *
   * @example
   * ```typescript
   * const blobUrl = fileUploadService.createBlobURL(file);
   * // ... utilisation ...
   * fileUploadService.revokeBlobURL(blobUrl); // Libère la mémoire
   * ```
   */
  public revokeBlobURL(blobURL: string): void {
    if (blobURL.startsWith('blob:')) {
      URL.revokeObjectURL(blobURL);
    }
  }

  /**
   * Lit un fichier comme DataURL (pour les images)
   * DEPRECATED: Utilisez createBlobURL() à la place pour de meilleures performances
   * Conservé pour compatibilité avec le code existant
   *
   * @param file - Le fichier à lire
   * @returns Promise qui se résout avec le DataURL
   */
  public readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Impossible de lire le fichier comme DataURL'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Erreur lors de la lecture du fichier'));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Lit un fichier comme texte (pour les JSON)
   *
   * @param file - Le fichier à lire
   * @returns Promise qui se résout avec le contenu texte
   */
  public readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Impossible de lire le fichier comme texte'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Erreur lors de la lecture du fichier'));
      };

      reader.readAsText(file);
    });
  }
}
